/**
 * Tâches 1283 / 1284 (Eric 2026-06-30) — Modale de nettoyage Objections & Q&R.
 *
 * Deux temps, jamais un seul : le scan PROPOSE (et explique) les suppressions,
 * l'humain coche ce qu'il accepte, et seules les lignes cochées sont effacées.
 * Décocher une ligne = refuser sa suppression.
 */
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { toast } from "sonner";

const LABELS = {
  objections: {
    fr: { title: "Nettoyage des objections", unit: "objection", units: "objections" },
    en: { title: "Objections cleanup", unit: "objection", units: "objections" },
  },
  qa: {
    fr: { title: "Nettoyage des Q&R", unit: "Q&R", units: "Q&R" },
    en: { title: "Q&A cleanup", unit: "Q&A", units: "Q&A" },
  },
};

export default function KnowledgeCleanupModal({
  open, onClose, kind = "objections", api, videoId, lang = "fr", onApplied,
}) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scan, setScan] = useState(null);
  const [selected, setSelected] = useState({});
  const [error, setError] = useState("");

  const t = (LABELS[kind] || LABELS.objections)[lang === "fr" ? "fr" : "en"];

  const runScan = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(
        `${api}/api/videos/${videoId}/${kind}/cleanup-scan`,
        { withCredentials: true, timeout: 180000 },
      );
      setScan(data);
      const next = {};
      (data?.candidates || []).forEach((c) => { next[c.ku_id] = true; });
      setSelected(next);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
      setScan(null);
    } finally {
      setLoading(false);
    }
  }, [api, videoId, kind]);

  useEffect(() => {
    if (open) runScan();
  }, [open, runScan]);

  if (!open) return null;

  const candidates = scan?.candidates || [];
  const acceptedIds = candidates.filter((c) => selected[c.ku_id]).map((c) => c.ku_id);

  const apply = async () => {
    if (!acceptedIds.length || applying) return;
    setApplying(true);
    try {
      const { data } = await axios.post(
        `${api}/api/videos/${videoId}/${kind}/cleanup-apply`,
        { ku_ids: acceptedIds },
        { withCredentials: true },
      );
      toast.success(lang === "fr"
        ? `${data?.deleted_count || 0} ${t.units} supprimée(s), ${candidates.length - acceptedIds.length} conservée(s)`
        : `${data?.deleted_count || 0} deleted, ${candidates.length - acceptedIds.length} kept`);
      onApplied?.(data);
      onClose?.();
    } catch (e) {
      toast.error((lang === "fr" ? "Nettoyage impossible : " : "Cleanup failed: ")
        + (e?.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      data-testid="knowledge-cleanup-modal"
    >
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-zinc-200">
          <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
            <span aria-hidden="true">🧹</span>
            {t.title}
          </h2>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700 text-xl leading-none"
            data-testid="cleanup-close-btn"
            aria-label="close"
          >×</button>
        </div>

        <div className="p-4 overflow-y-auto flex-1 space-y-3">
          {loading && (
            <div className="py-10 text-center text-sm text-zinc-600" data-testid="cleanup-loading">
              {lang === "fr" ? "Analyse en cours… (une passe IA vérifie la pertinence)" : "Scanning…"}
            </div>
          )}

          {!loading && error && (
            <div className="rounded border border-red-300 bg-red-50 p-3 text-xs text-red-800" data-testid="cleanup-error">
              {error}
            </div>
          )}

          {!loading && !error && candidates.length === 0 && (
            <div className="py-10 text-center text-sm text-zinc-600" data-testid="cleanup-empty">
              {lang === "fr"
                ? `Rien à nettoyer : les ${scan?.total_scanned ?? 0} ${t.units} analysées sont toutes exploitables.`
                : `Nothing to clean: all ${scan?.total_scanned ?? 0} entries look usable.`}
            </div>
          )}

          {!loading && !error && candidates.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-3 text-[11px]">
                <span className="text-zinc-700" data-testid="cleanup-summary">
                  {lang === "fr"
                    ? `${candidates.length} ${t.units} proposée(s) à la suppression sur ${scan?.total_scanned ?? 0} analysée(s). Décochez celles que vous voulez garder.`
                    : `${candidates.length} proposed for deletion out of ${scan?.total_scanned ?? 0}. Uncheck what you want to keep.`}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => {
                      const next = {};
                      candidates.forEach((c) => { next[c.ku_id] = true; });
                      setSelected(next);
                    }}
                    className="px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-100 font-semibold"
                    data-testid="cleanup-accept-all-btn"
                  >
                    {lang === "fr" ? "Tout accepter" : "Accept all"}
                  </button>
                  <button
                    onClick={() => setSelected({})}
                    className="px-2 py-1 rounded border border-zinc-300 bg-white hover:bg-zinc-100 font-semibold"
                    data-testid="cleanup-reject-all-btn"
                  >
                    {lang === "fr" ? "Tout refuser" : "Reject all"}
                  </button>
                </span>
              </div>

              <ul className="space-y-2">
                {candidates.map((c) => (
                  <li
                    key={c.ku_id}
                    className={`rounded border p-3 transition-colors ${
                      selected[c.ku_id] ? "border-red-300 bg-red-50/60" : "border-zinc-200 bg-white"
                    }`}
                    data-testid={`cleanup-row-${c.ku_id}`}
                  >
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!selected[c.ku_id]}
                        onChange={(e) => setSelected((s) => ({ ...s, [c.ku_id]: e.target.checked }))}
                        className="mt-0.5"
                        data-testid={`cleanup-checkbox-${c.ku_id}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-xs font-semibold text-zinc-900 break-words">
                          {c.text || c.title || "(sans texte)"}
                        </span>
                        {c.answer && (
                          <span className="block mt-1 text-[11px] text-zinc-600 break-words line-clamp-3">
                            {c.answer}
                          </span>
                        )}
                        <span className="block mt-1.5 text-[11px] font-medium text-red-800">
                          {lang === "fr" ? "Raison : " : "Reason: "}{c.reason}
                        </span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 p-4 border-t border-zinc-200">
          <button
            onClick={runScan}
            disabled={loading || applying}
            className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-semibold disabled:opacity-40"
            data-testid="cleanup-rescan-btn"
          >
            ↻ {lang === "fr" ? "Relancer l'analyse" : "Re-scan"}
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-zinc-300 bg-white hover:bg-zinc-100 text-xs font-semibold"
              data-testid="cleanup-cancel-btn"
            >
              {lang === "fr" ? "Annuler" : "Cancel"}
            </button>
            <button
              onClick={apply}
              disabled={!acceptedIds.length || applying}
              className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-bold disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cleanup-apply-btn"
            >
              {applying
                ? (lang === "fr" ? "Suppression…" : "Deleting…")
                : (lang === "fr" ? `Supprimer la sélection (${acceptedIds.length})` : `Delete selection (${acceptedIds.length})`)}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
